import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ContactPopup from '../components/ContactPopup';

const PricingCard = ({ plan, isRecommended, onGetStarted }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card 
      className={`relative border-2 transition-all duration-300 h-full flex flex-col ${
        isRecommended 
          ? 'border-blue-600 shadow-xl scale-105' 
          : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isRecommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 text-sm font-bold shadow-lg">
            RECOMMENDED
          </Badge>
        </div>
      )}
      
      <CardHeader className={`${isRecommended ? 'pt-8' : 'pt-6'} pb-6`}>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <div className="flex items-baseline mb-4">
          <span className="text-4xl font-bold text-blue-700">${plan.price}</span>
          <span className="text-gray-600 ml-2">+ Federal Filing Fee</span>
        </div>
        <p className="text-sm text-gray-500 italic">(Govt. filing fee not included)</p>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto max-h-80 mb-6">
          <ul className="space-y-3">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <Button
          onClick={onGetStarted}
          className={`w-full py-6 text-base font-semibold rounded-lg transition-all duration-300 ${
            isRecommended
              ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-50'
          }`}
        >
          {isHovered ? 'Start Registration' : 'Get Started'}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </CardContent>
    </Card>
  );
};

const PricingSection = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const pricingPlans = [
    {
      name: 'Essentials',
      price: 45,
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ]
    },
    {
      name: 'Enhanced',
      price: 149,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter'
      ]
    },
    {
      name: 'Deluxe',
      price: 299,
      isRecommended: true,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ]
    },
    {
      name: 'Platinum',
      price: 349,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Search Report',
        'High Priority Service',
        'Cease & Desist Letter'
      ]
    }
  ];

  return (
    <>
      <section id="pricing" className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Pricing Plans</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Affordable Trademark Registration Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your business needs. All plans include expert legal support and USPTO filing.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                plan={plan} 
                isRecommended={plan.isRecommended}
                onGetStarted={() => setIsPopupOpen(true)}
              />
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden overflow-hidden">
            <div className="overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-8 -mx-4 px-4">
              <div className="flex gap-6" style={{ width: 'max-content' }}>
                {pricingPlans.map((plan, index) => (
                  <div key={index} className="snap-center flex-shrink-0" style={{ width: '85vw', maxWidth: '400px' }}>
                    <PricingCard 
                      plan={plan} 
                      isRecommended={plan.isRecommended}
                      onGetStarted={() => setIsPopupOpen(true)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {pricingPlans.map((_, index) => (
                <div key={index} className="w-2 h-2 rounded-full bg-blue-300"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default PricingSection;
